import {EventEmitter, HostBinding, Inject, Injectable, LOCALE_ID, OnDestroy, OnInit, Input, Output, ViewChild} from '@angular/core';
import {ColDef} from 'ag-grid-community/dist/lib/entities/colDef';
import {GridOptions} from 'ag-grid-community/dist/lib/entities/gridOptions';
import {HttpClient} from '@angular/common/http';
import {
  BreadcrumbItem,
  GridComponent,
  GridRowSelection,
  GridStatusbarButton,
  GridVisibilityConfigModel,
  RestQueryParams,
  RestService,
  SettingWindowAction
} from 'gelios-ng-admin-components';
import {ActivatedRoute, Event, NavigationEnd, Router} from '@angular/router';
import {NotificationsService} from 'angular2-notifications';
import {AuthorizationService} from 'src/app/authorization/authorization.service';
import {BaseAvailableColumnsType} from './base-available-columns.type';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject, Subscription} from 'rxjs';
import {environment} from '../../environments/environment';
import {filter, map, share, shareReplay, startWith, switchMap, take, throttleTime, withLatestFrom} from 'rxjs/operators';
import {RouteLibraryService} from '../route/route-library.service';
import {MainLightboxService} from '../layout/main-lightbox.service';
import {GridToolButton} from 'gelios-ng-admin-components/lib/base-grid/grid-tools/grid-tool-button';
import {GridToolsFilterModel} from 'gelios-ng-admin-components/lib/base-grid/grid-tools/grid-tools-filter-model';
import {ModalWindowType} from '../shared/modal-window/modal-window-type';
import {ModalWindowService} from '../shared/modal-window/modal-window.service';
import {ProjectBusinessLogicSyncFilterService} from '../shared/project-business-logic-filter/project-business-logic-sync-filter.service';
import {BaseGridRestYiiFilterService} from '../shared/filter-grid/base-grid-rest-yii-filter-service';
import {updateRestFilterModel} from '../shared/project-business-logic-filter/project-business-logic-filter-utils';
import {CellClickedEvent, CellDoubleClickedEvent} from 'ag-grid-community';
import {RouteLibraryEntities} from '../route/route-library-entity';

@Injectable()
export abstract class BaseListComponent implements OnInit, OnDestroy {
  @HostBinding('class.section-content') true;
  @ViewChild('gridComponent') gridComponent: GridComponent;
  @Input() forDashboard = false;
  @Output() gridRefreshRequested: EventEmitter<GridComponent> = new EventEmitter<GridComponent>();
  idColumnName = 'id';
  model: Observable<any>;
  maxBlocksInCache = 4;
  maxConcurrentDatasourceRequests = 4;
  cacheBlockSize = 200;
  headerHeight = 32;
  floatingFiltersHeight = 32;
  paginationPageSize = 50;
  rowSelection: GridRowSelection = GridRowSelection.SINGLE;
  rowMultiSelectWithClick = false;
  gridOptions: Observable<GridOptions>;
  reloadModelSubject = new Subject<boolean>();
  gridVisibilityConfig: GridVisibilityConfigModel = {
    gridConfig: {
      grid: true,
      tools: true,
      statusBar: true,
    },
    settingWindow: {
      visible: true,
      hasFullscreenButton: true,
    },
  };
  gridReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  selectedIds: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>([]);
  protected breadcrumbsBaseParts: BehaviorSubject<BreadcrumbItem[]> = new BehaviorSubject([]);
  protected breadcrumbsAdditionalParts: BehaviorSubject<BreadcrumbItem[]> = new BehaviorSubject([]);
  breadcrumbItemsShareReplay: Observable<BreadcrumbItem[]>;
  // selectedRows: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>([]);
  subscriptions: Subscription[] = [];
  restFilterService = new BaseGridRestYiiFilterService();
  service: RestService<any>;
  title = 'Item sharing web admin';
  modelName: string;
  tableName: string;
  gridToolTitle = null;
  disableRowCount = false;
  rowsAreClickable = true;
  routeEntity: RouteLibraryEntities;
  settingWindowLockColumn = 'name';
  refreshTime = 0;
  exportSpecialTypes = {};
  exportSettings = {};
  crudAccess: Observable<{readAccess: boolean, updateAccess: boolean, deleteAccess: boolean}>;
  settingsActions: Observable<Array<SettingWindowAction> | null>;
  settingsActionsShareReplay: Observable<Array<SettingWindowAction> | null>;
  gridRestQueryParams: BehaviorSubject<RestQueryParams> = new BehaviorSubject<RestQueryParams>(null);
  gridToolFilterModel: BehaviorSubject<GridToolsFilterModel | null> = new BehaviorSubject<GridToolsFilterModel | null>(null);
  gridToolButtons: Array<GridToolButton> = [
    this.gridToolButtonAdd(),
  ];
  statusbarButtons: GridStatusbarButton[] = [
    this.refreshStatusbarButton(),
    this.resetFilterStatusbarButton(),
    this.exportStatusbarButton(),
  ];
  exportButtonClick: EventEmitter<boolean> = new EventEmitter<boolean>();
  exportQrcodeButtonClick: EventEmitter<boolean> = new EventEmitter<boolean>();
  _selectedItemIds: Array<number> = [];

  exportStatusbarButton(): GridStatusbarButton {
    return {
      title: 'Export',
      isVisible: () => true,
      iconName: () => 'grid-tools__icon_export',
      onClick: (): void => {
        const subscription = this.modalWindowService.showWindow({
          modalWindowType: ModalWindowType.Confirmation,
          text: this.translate.instant('Are you sure you want to export table data?')
        }).subscribe(result => {
          subscription.unsubscribe();
          if (result.confirmed) {
            this.exportButtonClick.emit(true);
          }
          this.modalWindowService.modalWindowResponse.next({confirmed: false});
        });
      },
    };
  }

  refreshStatusbarButton(): GridStatusbarButton {
    return {
      title: 'Refresh',
      isVisible: () => true,
      iconName: () => 'grid-tools__icon_refresh',
      onClick: () => this.refreshGrid()
    };
  }

  resetFilterStatusbarButton(): GridStatusbarButton {
    return {
      title: 'Reset',
      iconName: () => {
        let filterModel = {};
        let sortModel = [];
        if (this.gridComponent && this.gridComponent.gridApi) {
          filterModel = this.gridComponent.gridApi.getFilterModel();
          sortModel = this.gridComponent.gridApi.getSortModel();
        }
        return Object.values(filterModel).length > 0 || sortModel.length > 0
          ? 'grid-tools__icon_reset_applied' : 'grid-tools__icon_reset';
      },
      onClick: () => {
        this.gridComponent.gridApi.setFilterModel(null);
        this.gridComponent.gridApi.setSortModel(null);
        this.gridToolFilterModel.next(this.createGridFilterModel());
      },
      isVisible: () => true,
    };
  }

  exportQrcodeStatusbarButton(): GridStatusbarButton {
    return {
      title: 'Export qrcode',
      isVisible: () => true,
      iconName: () => 'grid-tools__icon_export-qrcode',
      onClick: (): void => {
        this.exportQrcodeButtonClick.emit(true);
      }
    };
  }

  gridToolButtonAdd(): GridToolButton {
    return {
      title: 'Add',
      buttonStyleType: 'success',
      onClick: (): void => {
        // this.onRefreshActionsEvent(null);
        this.route.navigate(
          ['create'],
          {relativeTo: this.activatedRoute}
        );
      },
      isVisible: () => this.authorizationService.getEntityAccessRightsInstant()[this.modelName]
        && this.authorizationService.getEntityAccessRightsInstant()[this.modelName].create,
    };
  }

  gridToolButtonImport(): GridToolButton {
    return {
      title: 'Import',
      buttonStyleType: 'default',
      onClick: (): void => {
        this.route.navigate(
          ['import'],
          {relativeTo: this.activatedRoute}
        );
      },
      isVisible: () => this.authorizationService.getEntityAccessRightsInstant()[this.modelName]
        && this.authorizationService.getEntityAccessRightsInstant()[this.modelName].create,
    };
  }
  public getSettingsActions(readAccess: boolean, updateAccess: boolean, deleteAccess: boolean, model?: any):
    Observable<Array<SettingWindowAction>> {
    const actions: Array<SettingWindowAction> = [];
    if (readAccess) {
      actions.push({
        title: 'View',
        route: ':id/view',
        name: 'View',
        icon: 'ic_set_menu_view',
      });
    }
    if (updateAccess) {
      actions.push({
        title: 'Edit',
        route: ':id/edit',
        name: 'Edit',
        icon: 'ic-edit',
        isTopMenu: true,
      });
    }
    if (deleteAccess) {
      actions.push(this.getDeleteButton());
    }
    return of(actions);
  }

  public getStatusbarButtons() {
    return this.statusbarButtons;
  }

  public getGridToolButtons() {
    return this.gridToolButtons;
  }

  get selectedItemIds() {
    return this._selectedItemIds;
  }

  abstract getAvailableColumns(): Observable<BaseAvailableColumnsType>;



  protected getColumnsAvailableToUser(): Observable<string[]> {
    return this.authorizationService.getEntityAccessRights().pipe(
      map(access => access[this.modelName].list)
    );
  }

  protected colDef(): Observable<Array<ColDef>> {
    return combineLatest(
      this.getColumnsAvailableToUser(),
      this.getAvailableColumns(),
    ).pipe(
      filter(
        ([columnsAvailableToUser, availableColumns]) => !!columnsAvailableToUser && !!availableColumns,
      ),
      map(
        ([columnsAvailableToUser, availableColumns]) => Object.keys(availableColumns)
          .filter(column => columnsAvailableToUser.findIndex(availableColumn => availableColumn === column) > -1)
          .map(column => availableColumns[column])
      )
    );
  }

  abstract getModelName(): string;

  abstract getTableName(): string;

  abstract createService(http: HttpClient, environmentVersion: string, environmentApiBasepoint: string): RestService<any>;

  constructor(
    protected authorizationService: AuthorizationService,
    public route: Router,
    public activatedRoute: ActivatedRoute,
    protected translate: TranslateService,
    protected notif: NotificationsService,
    protected http: HttpClient,
    public projectBusinessLogicSyncFilterService: ProjectBusinessLogicSyncFilterService,
    protected routeLibrary: RouteLibraryService,
    public mainLightboxService: MainLightboxService,
    @Inject('ENVIRONMENT_VERSION') protected environmentVersion: string,
    @Inject('ENVIRONMENT_API_BASEPOINT') protected environmentApiBasepoint: string,
    @Inject(LOCALE_ID) protected localeId: string,
    protected modalWindowService: ModalWindowService,
  ) {
    this.applyInitialGridFilter();
  }

  onRowSelectionChange(ids: Array<any>) {
    if (this.gridVisibilityConfig.settingWindow.visible && ids.length > 0) {
      this.selectedIds.next(ids);
    }
    // this.selectedRows.next(this.gridComponent.gridApi.getSelectedRows());
  }

  onRowClicked(ids: Array<any>) {
    if (this.gridVisibilityConfig.settingWindow.visible && ids.length > 0) {
      this.selectedIds.next(ids);
    }
    // this.selectedRows.next(this.gridComponent.gridApi.getSelectedRows());
  }

  onRowDoubleClicked(event: any) {
  }

  // public getModelToFillSettingsActions(id): Observable<any> {
  //   return this.http.get(
  //     environment.apiBasepoint + this.getModelName() + '/' + id + '?fields=id,name,model_access_rights'
  //   );
  // }

  protected getModelObservable(id) {
    return this.http.get(
      environment.apiBasepoint + this.getModelName() + '/' + id + '?fields=id,name,model_access_rights',
      {observe: 'body'}
    );
  }

  createGridFilterModel(): GridToolsFilterModel | null {
    return null;
  }

  applyInitialGridFilter() {
    const initialModel = this.createGridFilterModel() ? this.createGridFilterModel().onInitFilterModel() : {};
    this.restFilterService.filterModel = {
      ...this.restFilterService.filterModel,
      ...initialModel
    };
  }

  applyProjectFilterToGrid() {
    this.subscriptions.push(
      this.projectBusinessLogicSyncFilterService.getFormattedProjectBusinessLogicFilter()
        .subscribe((projectBusinessLogicFilter) => {
          this.restFilterService.filterModel = updateRestFilterModel(
            this.restFilterService.filterModel,
            projectBusinessLogicFilter
          );
          this.refreshGrid();
        })
    );
  }

  ngOnInit() {
    this.gridOptions = this.colDef().pipe(
      map(
        colDef => {
          const gridOptions: GridOptions = {
            columnDefs: colDef,
            maxBlocksInCache: this.maxBlocksInCache,
            maxConcurrentDatasourceRequests: this.maxConcurrentDatasourceRequests,
            cacheBlockSize: this.cacheBlockSize,
            headerHeight: this.headerHeight,
            floatingFiltersHeight: this.floatingFiltersHeight,
            paginationPageSize: this.paginationPageSize,
            suppressDragLeaveHidesColumns: true,
            onCellClicked: (event: CellClickedEvent) => {
              this.onCellClicked(event);
            },
            onGridSizeChanged: (gOptions: GridOptions) => {
              this.onGridSizeChanged(gOptions);
            },
            onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
              this.onCellDoubleClicked(event);
            },
          };
          return gridOptions;
        }
      )
    );
    this.breadcrumbItemsShareReplay = combineLatest(
      this.breadcrumbsBaseParts,
      this.breadcrumbsAdditionalParts
    ).pipe(
      map(
        ([baseParts, additionalParts]) => this.buildBreadcrumbItems(baseParts, additionalParts)
      ),
      shareReplay(1)
    );
    this.modelName = this.getModelName();
    this.tableName = this.getTableName();
    this.applyProjectFilterToGrid();
    this.service = this.createService(this.http, this.environmentVersion, this.environmentApiBasepoint);

    if (this.activatedRoute.snapshot.firstChild
      && this.activatedRoute.snapshot.firstChild.params
      && this.activatedRoute.snapshot.firstChild.params.id
      && this.gridVisibilityConfig.settingWindow.visible
    ) {
      const id = this.toIntegerIfIsUnsignedInteger(this.activatedRoute.snapshot.firstChild.params.id);
      this.selectedIds.next([id]);
    }

    this.model = combineLatest(
      this.selectedIds,
      this.reloadModelSubject.pipe(
        startWith(true),
        throttleTime(200)
      )
    ).pipe(
      switchMap(
        ([selectedIds,]) => selectedIds.length > 0 ?
          this.getModelObservable(selectedIds[0]) : of(null)
      ),
      share()
    );
    this.initBreadcrumbsSubscriptions();
    this.crudAccess = this.model.pipe(
      withLatestFrom(this.selectedIds),
      map(
        ([model, selectedIds]) => [model, selectedIds.length > 0 ? selectedIds[0] : null]
      ),
      // filter(
      //   ([model, id]) => !!model && !!id
      // ),
      switchMap(
        ([model, id]) => model && id ?
          forkJoin(
            this.authorizationService.checkAccess(this.modelName, 'read', id, model),
            this.authorizationService.checkAccess(this.modelName, 'update', id, model),
            this.authorizationService.checkAccess(this.modelName, 'delete', id, model),
          )
          : of([false, false, false])
      ),
      map(
        ([readAccess, updateAccess, deleteAccess]) => {
          return {readAccess: readAccess, updateAccess: updateAccess, deleteAccess: deleteAccess};
        }
      )
    );
    this.settingsActions = this.crudAccess.pipe(
      withLatestFrom(this.selectedIds, this.model),
      map(
        ([crudAccess, selectedIds, model]) => [crudAccess, selectedIds.length > 0 ? selectedIds[0] : null, model]
      ),
      // filter(
      //   ([crudAccess, model]) => !!crudAccess && !!model
      // ),
      switchMap(
        ([crudAccess, id, model]) => model && id ?
          this.getSettingsActions(
            crudAccess.readAccess,
            crudAccess.updateAccess,
            crudAccess.deleteAccess,
            model
          )
          : of(null)
      ),
      share()
    );
    this.settingsActionsShareReplay = this.settingsActions.pipe(shareReplay(1));
    this.subscriptions.push(
      this.settingsActions
        .pipe(
          withLatestFrom(
            this.selectedIds,
            this.crudAccess
          ),
          map(
            ([settingsActions, selectedIds, crudAccess]) =>
              [settingsActions, selectedIds.length > 0 ? selectedIds[0] : null, crudAccess]
          ),
          // filter(
          //   ([settingsActions, selectedId, crudAccess]) => !!selectedId
          // ),
        ).subscribe(
        ([settingsActions, selectedId, crudAccess]) => {
          let route = '';
          if (
            !this.activatedRoute.snapshot
            || !this.activatedRoute.snapshot.firstChild
            || !this.activatedRoute.snapshot.firstChild.params
            || !this.activatedRoute.snapshot.firstChild.params.id
            || this.toIntegerIfIsUnsignedInteger(this.activatedRoute.snapshot.firstChild.params.id) !== selectedId
          ) {
            if (this.routeEntity) {
              const hasView = RouteLibraryService.availableRoutes[this.routeEntity].view;
              if (crudAccess.readAccess && hasView) {
                this.route.navigateByUrl(this.routeLibrary.getRoute({
                  entity: this.routeEntity,
                  action: 'view',
                  id: selectedId
                }));
                return;
              }
              if (crudAccess.updateAccess) {
                this.route.navigateByUrl(this.routeLibrary.getRoute({
                  entity: this.routeEntity,
                  action: 'update',
                  id: selectedId
                }));
                return;
              }
            }
            if (crudAccess.updateAccess) {
              route = selectedId + '/edit';
            }
          }
          if (route) {
            this.route.navigate(
              [route],
              {relativeTo: this.activatedRoute}
            );
          }
        }
      )
    );
    this.initRouteChangeSubscription();
    this.updateBreadcrumbsAdditionalParts();
    this.subscriptions.push(
      this.settingsActionsShareReplay.subscribe()
    );
    this.subscriptions.push(
      this.breadcrumbItemsShareReplay.subscribe()
    );
  }

  protected toIntegerIfIsUnsignedInteger(value) {
    return /^[+-]?[0-9]+$/.test(value) ? parseInt(value) : value;
  }

  protected buildBreadcrumbItems(baseParts: BreadcrumbItem[], additionalParts: BreadcrumbItem[]) {
    return baseParts ?
      [
        ...baseParts,
        ...additionalParts
      ]
      : [];
  }

  protected initRouteChangeSubscription() {
    this.subscriptions.push(
      this.route.events
        .pipe(withLatestFrom(this.selectedIds)).subscribe(
        ([event, selectedIds]) => {
          if (event instanceof NavigationEnd) {
            if (
              this.activatedRoute.snapshot
              && this.activatedRoute.snapshot.firstChild
              && this.activatedRoute.snapshot.firstChild.params
            ) {
              const routeId = this.activatedRoute.snapshot.firstChild.params.id;
              if (routeId) {
                const idInt = this.toIntegerIfIsUnsignedInteger(routeId);
                if (selectedIds.length === 0 || selectedIds[0] !== idInt) {
                  this.selectedIds.next([idInt]);
                }
              } else {
                if (selectedIds.length > 0) {
                  this.selectedIds.next([]);
                }
              }
            }
            this.updateBreadcrumbsAdditionalParts();
          }
        }
      )
    );
  }

  getActivatedRouteData() {
    return this.activatedRoute
      && this.activatedRoute.snapshot
      && this.activatedRoute.snapshot.firstChild
      && this.activatedRoute.snapshot.firstChild.data || null;
  }

  getActivatedRouteTitle(): string | null {
    const data = this.getActivatedRouteData();
    return data && data['title'] || null;
  }

  getActivatedRouteIconClass(): string | null {
    const data = this.getActivatedRouteData();
    return data && data['icon'] || null;
  }

  protected updateBreadcrumbsAdditionalParts() {
    const iconClass = this.getActivatedRouteIconClass();
    const activatedRouteTitle = this.getActivatedRouteTitle();

    if (activatedRouteTitle) {
        this.setBreadcrumbAdditionalParts([{
          caption: this.translate.instant(activatedRouteTitle),
          arrow: true,
          icon: iconClass
        }]);
    } else {
      this.setBreadcrumbAdditionalParts([]);
    }
  }

  // getModelToFillSettingsActionsObservable(id: string) {
  //   return this.getModelToFillSettingsActions(id)
  //     .pipe(
  //       switchMap((model) => forkJoin(
  //         this.authorizationService.checkAccess(this.modelName, 'read', id, model),
  //         this.authorizationService.checkAccess(this.modelName, 'update', id, model),
  //         this.authorizationService.checkAccess(this.modelName, 'delete', id, model),
  //         of(model)
  //       )),
  //       switchMap(
  //         ([readAccess, updateAccess, deleteAccess, model]) => {
  //           return forkJoin(
  //             of(readAccess),
  //             of(updateAccess),
  //             of(deleteAccess),
  //             this.getSettingsActions(readAccess, updateAccess, deleteAccess, model).pipe(take(1))
  //           );
  //         }
  //       ),
  //     );
  // }

  ngOnDestroy() {
    this.subscriptions.filter(s => !!s).forEach(s => s.unsubscribe());
  }

  ready(): boolean {
    const userAccessRights = this.authorizationService.getEntityAccessRightsInstant();
    const ready = userAccessRights
      && userAccessRights[this.modelName]
      && userAccessRights[this.modelName].list
      && this.settingsActionsShareReplay
      && this.breadcrumbItemsShareReplay
    ;
    return !!ready;
  }

  onSettingWindowClose(event) {
    this.route.navigate(
      ['./'],
      {relativeTo: this.activatedRoute}
    );
    this.selectedIds.next([]);
  }

  onRefreshActionsEvent() {
    this.reloadModelSubject.next(true);
  }

  onRefreshBreadcrumbsEvent() {
    this.reloadModelSubject.next(true);
  }

  onRefreshDataFromSettingWindow(): void {
    this.gridRefreshRequested.emit(this.gridComponent);
  }

  refreshGrid(): void {
    if (this.gridComponent) {
      this.gridComponent.refreshGrid();
    }
  }

  protected t(key: string): string {
    return this.translate.instant(key);
  }

  protected getDeleteButton(): SettingWindowAction {
    return {
      title: 'Delete',
      route: 'false', // if false - no route
      name: 'Delete',
      icon: 'ic-delete',
      isTopMenu: true,
      onClick: e => this.doDelete(e),
    };
  }

  doDelete(event: Event) {
    if (window.confirm(this.t('Are sure you want to delete this item') + '?')) {
      if (this.activatedRoute.snapshot && this.activatedRoute.snapshot.firstChild.params) {
        this.service.delete(parseInt(this.activatedRoute.snapshot.firstChild.params.id, 10)).subscribe(() => {
            this.onSettingWindowClose(event);
            this.refreshGrid();
            this.notif.success(this.t('Removed'));
          },
          () => {
            this.notif.error(this.t('Error'));
          }
        );
      }
    }
  }

  onQueryParamsChanged(restQueryParams: RestQueryParams) {
    this.gridRestQueryParams.next(restQueryParams);
  }

  onExportError($event) {
    this.notif.error(this.translate.instant($event.message) + ' (' + this.translate.instant('Limit') + ' ' + $event.limit + ')');
  }

  onGridReady(event) {
    this.gridReady.next(true);
    this.gridToolFilterModel.next(this.createGridFilterModel());
    if (this.gridComponent && this.gridComponent.gridStatusBar) {
      this.getAvailableColumns().pipe(filter(ac => !!ac), take(1)).subscribe(
        availableColumns => {
          this.gridComponent.gridStatusBar.columnEditorEnabled = true;
          this.gridComponent.gridStatusBar.tableName = this.tableName;
          this.gridComponent.gridStatusBar.availableColumns = Object.keys(availableColumns);
        }
      );
    }
  }

  initBreadcrumbsSubscriptions() {
    if (this.gridVisibilityConfig.settingWindow.visible) {
      const iconClass = this.getActivatedRouteIconClass();
      this.subscriptions.push(
        this.model.subscribe(
          model => {
            const breadcrumbItems = model ?
              [
                {
                  caption: 'ID: ' + model.id,
                  icon: iconClass
                },
                {
                  caption: model.name,
                },
              ]
              : [];
            this.setBreadcrumbBaseParts(breadcrumbItems);
          }
        )
      );
    }
  }

  protected setBreadcrumbAdditionalParts(parts: BreadcrumbItem[]) {
    this.breadcrumbsAdditionalParts.next(parts);
  }
  protected setBreadcrumbBaseParts(parts: BreadcrumbItem[]) {
    this.breadcrumbsBaseParts.next(parts);
  }

  onCellClicked(event: CellClickedEvent) { }
  onGridSizeChanged(gOptions: GridOptions) { }

  onCellDoubleClicked(event: CellDoubleClickedEvent) { }

  closeToolEvent() {
  }

  onModelUpdated() {
  }
}
